import * as React from "react";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {DirectDebitInformationModal} from "../../../../../components/directDebit/DirectDebitInformationModal";
import {
    AnnualPolicyInfo,
    Mandate,
    Policy,
    ACCOUNT_NAME_REGEX,
    ACCOUNT_NUMBER_REGEX,
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    SORT_CODE_REGEX,
    AnnualPolicyPremiumTypeEnum
} from "@ferry/journey-services";
import {lazyInject} from "@athena-portal/core";
import {Checkbox} from "../../../../../components/inputs/Checkbox";
import {DirectDebitGuarantee} from "../../../../../components/directDebit/DirectDebitGuarantee";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {PageTitle} from "../../../../../components/navigation/PageTitle";
import {getInputClassName} from "../../../../../CommonFunctions";
import {TextInputWithoutLabel} from "../../../../../components/inputs/TextInputWithoutLabel";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {AjaxError} from "rxjs/ajax";
import {WarningModal} from "../../../../../components/modals/WarningModal";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {formatSortCode, SortCodeInput, resourcesHelper, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    annualPolicyInfo?: AnnualPolicyInfo,
    newMandate: Mandate,
    sortCodePartOne: string,
    sortCodePartTwo: string,
    sortCodePartThree: string,
    confirmDataChecked?: boolean,
    accountNumberIsValid?: boolean
    sortCodeIsValid?: boolean
    showBankValidationFailed: boolean
    showOverlay: boolean
    invalidInputFields: string[],
    modalOpened: boolean,
    accountNameModalOpened: boolean,
    product: string,
    ddUnsupported: boolean,
    brandName: string,
    premiumType?: string,
}

export class DirectDebitChangePagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pluginTemplates = new PluginTemplates();
    private readonly policyNumber: string;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.policyNumber = this.props.params.policyNumber;

        this.state = {
            sortCodePartOne: "",
            sortCodePartTwo: "",
            sortCodePartThree: "",
            newMandate: new Mandate(),
            showBankValidationFailed: false,
            showOverlay: false,
            invalidInputFields: [],
            modalOpened: false,
            accountNameModalOpened: false,
            product: "",
            ddUnsupported: false,
            brandName: ""
        }

        this.handleAccountNumber = this.handleAccountNumber.bind(this)
        this.setSortCodePartOne = this.setSortCodePartOne.bind(this)
        this.setSortCodePartTwo = this.setSortCodePartTwo.bind(this)
        this.setSortCodePartThree = this.setSortCodePartThree.bind(this)
        this.handleConfirmData = this.handleConfirmData.bind(this)
        this.submitChanges = this.submitChanges.bind(this)
        this.mergeSortCodeEvent = this.mergeSortCodeEvent.bind(this)
        this.validateSortCodePartInput = this.validateSortCodePartInput.bind(this)
        this.handleForm = this.handleForm.bind(this)
        this.closeBankValidationFailModal = this.closeBankValidationFailModal.bind(this)
        this.removeInvalidField = this.removeInvalidField.bind(this)
        this.scrollToElement = this.scrollToElement.bind(this)
        this.process = this.process.bind(this)
        this.validateAccountHolderName = this.validateAccountHolderName.bind(this)
        this.handleMoveBack = this.handleMoveBack.bind(this)
        this.toggle = this.toggle.bind(this)
        this.toggleAndRemoveFlag = this.toggleAndRemoveFlag.bind(this)
        this.toggleAccountNameModal = this.toggleAccountNameModal.bind(this)
    }

    public componentDidMount(): void {

        window.scrollTo(0, 0);

        this.pluginTemplates.setPageTitle("Update your direct debit details");

        this.customerPortalService
            .getAnnualPolicyInfo(this.policyNumber)
            .subscribe((annualPolicyInfo: AnnualPolicyInfo) => {
                const mandate: Mandate = this.state.newMandate;
                mandate.accountName = annualPolicyInfo.mandates.sort((firstMandate: any, secondMandate: any) => secondMandate.id - firstMandate.id)[0].accountName;
                this.setState({
                        annualPolicyInfo,
                        newMandate: mandate,
                        premiumType: annualPolicyInfo.premiumType
                    }
                )
            });

        this.customerPortalService
            .getPolicy(this.policyNumber)
            .subscribe((policy: Policy) => {
                    this.setState({
                        brandName: policy.interaction.brandName,
                        product: policy.interaction.product
                    })
                }
            );
    }

    public render() {

        return (
            <>
                <div className={"container-fluid"}>
                    <div className={"container pt-3 pb-3"}>
                        <FalconHeader/>
                        <div className="plugin">
                            {this.pluginTemplates.renderBackReloadLink("Back", true, this.getBackLinkByPremiumType())}
                            <PageTitle title={"Update your direct debit details"}
                                       className={"text-group title plugin no-indent-desktop"}/>
                            <div className="panel bg-white overflow-hidden p-3">
                                <DirectDebitInformationModal
                                    product={this.state.product}
                                    policyNumber={this.props.params.policyNumber}
                                    brandName={this.state.brandName}
                                />
                                {this.renderAccount()}
                                <DirectDebitGuarantee/>
                            </div>
                            <Button className={"btn btn-primary btn-next-no-arrow btn-wide"}
                                    type={"button"}
                                    onClick={this.handleForm}>Save Direct Debit details
                            </Button>
                            <Button type="button" className="btn btn-back" onClick={this.handleMoveBack}>Cancel changes</Button>
                            <div>
                                {this.pluginTemplates.renderBackReloadLink("Back", false, this.getBackLinkByPremiumType())}
                            </div>
                        </div>
                        <Footer/>
                    </div>
                </div>
                <Overlay show={this.state.showOverlay}/>
                {this.state.showBankValidationFailed &&
                    <WarningModal
                        isOpen={this.state.showBankValidationFailed}
                        onModalHide={this.closeBankValidationFailModal}
                        id={"bankValidationFailModal"}
                        title={"The bank details you’ve entered are incorrect."}
                        body={this.getBankValidationFailModalBody()}/>
                }
            </>
        );
    }

    public renderAccount() {

        const sortCodeValidationMessage = !!this.state.newMandate?.sortCode ?
            <div className="invalid-feedback d-block">{this.state.ddUnsupported ? 'We\'re unable to set up a Direct Debit with the bank details entered (some bank accounts do not support Direct Debit). ' +
                'Please use a different account to pay monthly or select the option to pay annually.' : 'The bank details you\'ve entered are incorrect.\n' +
                '                Please check the sort code and account number provided.\n' +
                '                Alternatively please choose the credit/debit card payment option.'}</div> :
            <div className="invalid-feedback d-block">Please answer this to carry on</div>

        return (
            <React.Fragment>
                <div className="pt-4">
                    <p className="font-weight-semi-bold mb-3">Name of account holder</p>
                    <p>{this.state.newMandate.accountName} <span onClick={this.toggleAccountNameModal} className="font-weight-semi-bold text-decoration-underlinecursor-pointer ml-2">Need to change this?</span></p>
                </div>
                {this.renderModalAccountNameModal()}

                <div className="form-group mb-0">
                    <div className="justify-content-between align-items-start">
                        <div>
                            <label className="mb-3" id="accountNumber" htmlFor="accountNumberInput">Account number</label>
                            <div className="d-flex">
                                <TextInputWithoutLabel
                                    id="accountNumberInput"
                                    value={this.state.newMandate.accountNumber}
                                    onChange={this.handleAccountNumber}
                                    minLength={8}
                                    maxLength={8}
                                    className={getInputClassName(this.state.accountNumberIsValid) + " account-number-input-width"}
                                    inputValid={this.state.accountNumberIsValid}
                                    validationMessage={!!this.state.newMandate?.accountNumber ? "The account number you've entered is incorrect." : "Please answer this to carry on"}
                                />
                            </div>
                            <div className="pb-2">
                                <p className="font-weight-light"> This is normally found on your debit card.
                                    It is a sequence of 8 digits.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <SortCodeInput
                    sortCodePartOne={this.state.sortCodePartOne}
                    sortCodePartTwo={this.state.sortCodePartTwo}
                    sortCodePartThree={this.state.sortCodePartThree}
                    sortCodeIsValid={this.state.sortCodeIsValid}
                    validationMessage={sortCodeValidationMessage}
                    onPartOneChange={this.setSortCodePartOne}
                    onPartTwoChange={this.setSortCodePartTwo}
                    onPartThreeChange={this.setSortCodePartThree}
                    onBlur={this.mergeSortCodeEvent}/>
                <Checkbox
                    label={<p className={"font-weight-600"}>I confirm, the name of the account holder,
                        account number and sort code are correct.
                        I am also the only person authorised to set up Direct Debits from this account.</p>}
                    name={"confirmDataCheckbox"}
                    id={"confirmDataCheckbox"}
                    value={!!this.state.confirmDataChecked}
                    className={"mt-4"}
                    onChanged={this.handleConfirmData}/>
                {this.state.confirmDataChecked === false &&
                    <div className="invalid-feedback d-block">Please answer this to carry on</div>}
                {this.isAccountDetailsSet() ? this.renderPositiveModal() : this.renderNegativeModal()}
            </React.Fragment>
        );
    }

    private renderPositiveModal(): any {
        const closeBtn = <button className="btn-close" onClick={this.toggleAndRemoveFlag}/>
        const mandate: any = this.state.newMandate;
        return (
            <Modal className="my-2 info-modal" isOpen={this.state.modalOpened} toggle={this.toggleAndRemoveFlag}>
                <ModalHeader toggle={this.toggleAndRemoveFlag} close={closeBtn}>
                    <div className="info-popup-title">
                        <img src={`/${resourcesHelper.THEME_PATH}/img/info-popup.svg`} alt="Attention"/>
                        <div className="text-blue-2 font-weight-medium">Please confirm your bank account details are correct</div>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <p className="mb-0">Name of account holder: <span className="font-weight-bold">{mandate.accountName}</span></p>
                    <p className="mb-0">Account number: <span className="font-weight-bold">{mandate.accountNumber}</span></p>
                    <p className="mb-0">Sort code: <span className="font-weight-bold">{formatSortCode(mandate.sortCode)}</span></p>
                    <p className="font-weight-bold">Are the above bank account details correct?</p>
                </ModalBody>
                <ModalFooter className="d-flex flex-column modal-footer">
                    <div className="row">
                        <button type="button" className="btn-white width-md-250" onClick={this.toggleAndRemoveFlag}>No, amend my details</button>
                        <button type="button" className="btn-primary width-md-250 mb-1 btn-next-no-arrow" onClick={this.toggle}>Yes, my details are correct</button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    private renderNegativeModal(): any {
        const closeBtn = <button className="btn-close" onClick={this.toggleAndRemoveFlag}/>
        return (
            <Modal className="my-2 info-modal" isOpen={this.state.modalOpened} toggle={this.toggleAndRemoveFlag}>
                <ModalHeader toggle={this.toggleAndRemoveFlag} close={closeBtn}>
                    <div className="d-flex">
                        <img className="d-block mr-3 mb-1 info-button-width" src={`/${resourcesHelper.THEME_PATH}/img/alert-popup.svg`} alt="Warning icon"/>
                        <div className="text-blue-2 font-weight-medium">Please check your bank account details</div>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0 font-weight-bold">
                    <p>The bank account details you've entered are incomplete or incorrect. Please check them and try again.</p>
                </ModalBody>
                <ModalFooter className="d-flex flex-column modal-footer align-self-end">
                    <button type="button" className="btn btn-primary width-md-250 mb-1 btn-next-no-arrow" onClick={this.toggleAndRemoveFlag}>Ok, I understand</button>
                </ModalFooter>
            </Modal>
        );
    }

    private renderModalAccountNameModal(): any {
        const closeBtn = <button className="btn-close" onClick={this.toggleAccountNameModal}/>
        return (
            <Modal className="my-2 info-modal" isOpen={this.state.accountNameModalOpened} toggle={this.toggleAccountNameModal}>
                <ModalHeader toggle={this.toggleAccountNameModal} close={closeBtn}>
                    <div className="d-flex">
                        <img className="d-block mr-3 mb-1 info-button-width" src={`/${resourcesHelper.THEME_PATH}/img/alert-popup.svg`} alt="Warning icon"/>
                        <div className="text-blue-2 font-weight-medium">Sorry...</div>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <p className="font-weight-semi-bold mb-3">We can only set up direct debits from the policyholder’s personal bank account.</p>
                    <p>If this isn’t possible, please choose another payment method.</p>
                </ModalBody>
                <ModalFooter>
                    <div className="row">
                        <button type="button" className="btn btn-primary width-md-250 mb-1 btn-next-no-arrow" onClick={this.toggleAccountNameModal}>Ok, I understand</button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    private readonly toggleAccountNameModal = () => {
        this.setState((state: IState) => ({
            accountNameModalOpened: !state.accountNameModalOpened
        }));
    }

    private isAccountDetailsSet(): boolean {
        return !!this.state.accountNumberIsValid && !!this.state.sortCodeIsValid && !!this.state.confirmDataChecked;
    }

    private toggle = () => {
        this.setState((state: IState) => ({
            modalOpened: !state.modalOpened
        }));
    }

    private toggleAndRemoveFlag = () => {
        this.setState((state: IState) => ({
            modalOpened: !state.modalOpened,
            confirmDataChecked: false
        }));
    }


    private handleAccountNumber(event: any): void {
        const mandate: Mandate = this.state.newMandate;
        mandate.accountNumber = event.target.value.replace(/[^0-9]/g, '');
        const accountNumberValid: boolean = ACCOUNT_NUMBER_REGEX.test(mandate.accountNumber);
        if (accountNumberValid) {
            this.removeInvalidField("accountNumber");
        }
        this.setState({
            newMandate: mandate,
            accountNumberIsValid: accountNumberValid
        })
    }

    private setSortCodePartOne(event: any): void {
        const mandate: Mandate = this.state.newMandate;
        const code = event.target.value.replace(/[^0-9]/g, '');
        const sortCode = code + this.state.sortCodePartTwo + this.state.sortCodePartThree;
        this.validateSortCodePartInput(sortCode);
        mandate.sortCode = sortCode;
        this.setState({
            newMandate: mandate,
            sortCodePartOne: code
        });
    }

    private setSortCodePartTwo(event: any): void {
        const mandate: Mandate = this.state.newMandate;
        const code = event.target.value.replace(/[^0-9]/g, '');
        const sortCode = this.state.sortCodePartOne + code + this.state.sortCodePartThree;
        this.validateSortCodePartInput(sortCode);
        mandate.sortCode = sortCode;
        this.setState({
            newMandate: mandate,
            sortCodePartTwo: code
        });
    }

    private setSortCodePartThree(event: any): void {
        const mandate: Mandate = this.state.newMandate;
        const code = event.target.value.replace(/[^0-9]/g, '');
        const sortCode = this.state.sortCodePartOne + this.state.sortCodePartTwo + code;
        this.validateSortCodePartInput(sortCode);
        mandate.sortCode = sortCode;
        this.setState({
            newMandate: mandate,
            sortCodePartThree: code
        });
    }

    private validateSortCodePartInput(sortCode: string): void {
        const isValid = SORT_CODE_REGEX.test(sortCode);
        if (sortCode) {
            this.removeInvalidField("accountSortCode");
        }
        this.setState({
            sortCodeIsValid: isValid
        })
    }

    private validateAccountHolderName(name: string): boolean {
        const allowedSpecialChars = ["-", "'"]
        let noInvalidPairs = true;

        for(const firstChar of allowedSpecialChars){
            for (const secondChar of allowedSpecialChars){
                if(name.includes(firstChar.concat(secondChar))){
                    noInvalidPairs = false;
                }
            } if (!noInvalidPairs) { break; }
        }
        return ACCOUNT_NAME_REGEX.test(name)
            && noInvalidPairs;
    }

    private removeInvalidField(key: string): void {
        if (this.state.invalidInputFields) {
            this.setState({
                invalidInputFields: this.state.invalidInputFields.filter(q => q !== key)
            })
        }
    }

    private closeBankValidationFailModal(): void {
        this.setState({
            showBankValidationFailed: !this.state.showBankValidationFailed
        })
    }

    private getBankValidationFailModalBody(): React.ReactNode {
        return (
            <p>
                Please check the sort code and account number provided.
            </p>
        )
    }

    private mergeSortCodeEvent(): void {
        const mandate: Mandate = this.state.newMandate;
        mandate.sortCode = this.state.sortCodePartOne + this.state.sortCodePartTwo + this.state.sortCodePartThree;
        this.setState({
            newMandate: mandate
        })
    }

    private handleConfirmData(value: boolean): void {
        if (value) {
            this.removeInvalidField("confirmDataCheckbox");
        }
        this.setState({
            confirmDataChecked: value,
            modalOpened: value
        })
    }

    private scrollToElement() {
        const invalidQuestions = this.state.invalidInputFields;
        if (invalidQuestions.length > 0) {
            const firstElement = invalidQuestions[0];
            const element = document.getElementById(firstElement);
            if (element !== null) {
                scrollTo(element.getBoundingClientRect().left, element.getBoundingClientRect().top + window.scrollY);
            }
        }
    }

    private handleForm() {
        const invalidFields: string[] = [];
        const mandate = this.state.newMandate;
        const checkBoxChecked = !!this.state.confirmDataChecked;

        if (!this.state.accountNumberIsValid) {
            invalidFields.push("accountNumber");
        }
        if (!this.state.sortCodeIsValid) {
            invalidFields.push("accountSortCode");
        }
        if (!this.state.confirmDataChecked) {
            invalidFields.push("confirmDataCheckbox");
        }

        this.setState({
            invalidInputFields: invalidFields.length > 0 ? invalidFields.slice(0,1) : invalidFields,
            accountNumberIsValid: mandate.accountNumber ? ACCOUNT_NUMBER_REGEX.test(mandate.accountNumber) : false,
            sortCodeIsValid: mandate.sortCode ? SORT_CODE_REGEX.test(mandate.sortCode) : false,
            confirmDataChecked: checkBoxChecked,
        }, () => {
            this.process()
        });
    }

    private process() {
        const isFormValid = !!this.state.accountNumberIsValid
            && !!this.state.sortCodeIsValid
            && !!this.state.confirmDataChecked;
        if (isFormValid) {
            this.submitChanges()
        } else {
            this.scrollToElement();
        }
    }

    private submitChanges(): void {
        this.setState({
            showOverlay: true
        })
        this.customerPortalService.updateDirectDebit(this.policyNumber, this.state.newMandate)
            .subscribe(
                (response: any) => {
                    if (response instanceof AjaxError) {
                        if (response.status === 409) {
                            this.setState({
                                showBankValidationFailed: true,
                                accountNumberIsValid: false,
                                sortCodeIsValid: false,
                                showOverlay: false
                            })
                        }
                    } else if (!response.valid && response.ddUnsupported) {
                            this.setState({
                                ddUnsupported: response.ddUnsupported,
                                showBankValidationFailed: true,
                                accountNumberIsValid: false,
                                sortCodeIsValid: false,
                                showOverlay: false
                            })
                    } else {
                        window.sessionStorage.setItem("showPopup", "true");
                        window.sessionStorage.setItem("referrerJourney", INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT);
                        this.props.navigate(this.getBackLinkByPremiumType());
                        this.setState({
                            showOverlay: false,
                            showBankValidationFailed: false
                        })
                    }
                }, () => {
                    this.setState({
                        showOverlay: false
                    });
                    console.log("Something went wrong");
                });
    }

    private handleMoveBack() {
        return this.props.navigate((this.getBackLinkByPremiumType()));
    }

    private readonly getBackLinkByPremiumType = (): string => {
        const policyNumber = this.props.params.policyNumber;

        return this.state.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            ? `/dashboard/monthlyPaymentHistory/${policyNumber}`
            : `/dashboard/annualPaymentHistory/${policyNumber}`;
    };
}

export const DirectDebitChangePage = withRouter<{}, IParams>(DirectDebitChangePagePlain)
